import { useState, useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { getSubdomainAvailability } from '../api/signup-service.js';
import { isValidSubdomain } from '../utils/validation-utils.js';

const cache = {};

const useSubdomainAvailability = (subdomain, shouldCheck = true) => {
  const [status, setStatus] = useState({
    available: null,
    loading: false,
    error: null,
  });

  const debouncedCheck = useMemo(
    () =>
      debounce((subdomain) => {
        getSubdomainAvailability({ subdomain })
          .then((response) => {
            const isAvailable = response?.data?.available;

            cache[subdomain] = isAvailable;
            
            setStatus({
              available: isAvailable,
              loading: false,
              error: null,
            });
          })
          .catch((error) => {
            setStatus({ available: null, loading: false, error });
          });
      }, 1000),
    []
  );

  useEffect(() => {
    if (!shouldCheck || !subdomain || !isValidSubdomain(subdomain)) {
      setStatus({ available: null, loading: false, error: null });
      debouncedCheck.cancel();
      return;
    }

    if (cache[subdomain] !== undefined) {
      setStatus({ available: cache[subdomain], loading: false, error: null });
      debouncedCheck.cancel();
      return;
    }

    setStatus((prev) => ({ ...prev, loading: true }));
    debouncedCheck(subdomain);

    return () => {
      debouncedCheck.cancel();
    };
  }, [subdomain, shouldCheck, debouncedCheck]);

  return status;
};

export default useSubdomainAvailability;